import { useMemo } from 'react';

import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';

import { ReviewInstanceStatus } from '@/constants/review/ReviewInstanceStatus';
import { usePerformanceTier } from '@/hooks/performance-tier/usePerformanceTier';

interface UseAssessmentReadOnlyResult {
    isCompleted: boolean;
    /**
     * The company no longer has Performance PRO: its Manager Assessments stay readable as they are,
     * but nothing on them can be changed or saved, completed or not.
     */
    isLockedByPlan: boolean;
    isReadOnly: boolean;
    canEdit: boolean;
    canUndo: boolean;
}

export const useAssessmentReadOnly = (
    cycleInstance?: CycleInstance | null,
): UseAssessmentReadOnlyResult => {
    const { isPro } = usePerformanceTier();

    return useMemo(() => {
        const isCompleted =
            cycleInstance?.reviewInstances?.some(
                (reviewInstance) => reviewInstance.status === ReviewInstanceStatus.COMPLETED,
            ) ?? false;
        const isLockedByPlan = !isPro;
        const isReadOnly = isCompleted || isLockedByPlan;

        return {
            isCompleted,
            isLockedByPlan,
            isReadOnly,
            canEdit: !isReadOnly,
            canUndo: isCompleted && !isLockedByPlan,
        };
    }, [cycleInstance, isPro]);
};
